import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { getApiUrl } from '../config/api';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Add a small delay to ensure server is ready
    const connectWithDelay = () => {

      
      // Create socket connection
      const newSocket = io(getApiUrl(''), {
        auth: {
          token: token
        },
        path: '/socket.io/', // Explicitly specify Socket.IO path
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        maxReconnectionAttempts: 5,
        forceNew: true, // Force new connection
        // Add timeout settings
        timeout: 20000,
        // Add upgrade timeout
        upgradeTimeout: 10000,
        // Add ping settings
        pingTimeout: 60000,
        pingInterval: 25000
      });

    // Socket event handlers
    newSocket.on('connect', () => {
      setConnected(true);
      
      // Get user ID from token and join user room
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.userId) {
          setUserId(payload.userId);
          newSocket.emit('join-user', payload.userId);
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    });

    // Monitor transport changes
    newSocket.io.engine.on('upgrade', () => {
      console.log('🔄 Transport upgraded to:', newSocket.io.engine.transport.name);
    });

    newSocket.io.engine.on('upgradeError', (error) => {
      console.warn('⚠️ Transport upgrade failed, continuing with polling:', error.message);
      // Don't treat this as a fatal error - polling still works
    });

    // Handle WebSocket connection errors specifically
    newSocket.io.engine.on('error', (error) => {
      console.error('Socket.IO engine error:', error);
    });

    newSocket.on('disconnect', () => {
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Dashboard WebSocket connection error:', error);
      console.error('Error details:', {
        message: error.message,
        type: error.type,
        description: error.description,
        context: error.context,
        transport: error.transport
      });
      setConnected(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setConnected(true);
      
      // Re-join user room after reconnection
      if (userId) {
        newSocket.emit('join-user', userId);
      }
    });

      setSocket(newSocket);

      // Cleanup on unmount
      return () => {
        newSocket.close();
      };
    };

    // Add a 1-second delay before connecting
    const timeoutId = setTimeout(connectWithDelay, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Function to emit events to the server
  const emit = (event, data) => {
    if (socket && connected) {
      socket.emit(event, data);
    }
  };

  // Function to listen for events
  const on = (event, callback) => {
    if (socket) {
      socket.on(event, callback);
      return () => socket.off(event, callback);
    }
    return () => {};
  };

  const value = {
    socket,
    connected,
    userId,
    emit,
    on
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
